import { AxiosHeaders, AxiosRequestHeaders, default as axios } from "axios";
import { HermesError, ModalTypes, PageName, ProductGame } from "@/@types/common";
import { VirtualCurrencyCodeByProductGame } from "@/@types/currencies";
import { AnalyticsEvent, sendAnalytics } from "@/utils/analytics";
import logger from "@/utils/logger";
import siteConfig, { GAME_NAME_BY_GAMEID } from "@/variables";
import { PurchaseData } from "@/@types/purchase";
import {
  ConfirmOrderV3Request,
  ConfirmOrderV3Response,
  OrderContext,
  StartOrderV3Request,
  StartOrderV3Response,
  StartVirtualCurrencyOrderV3Request,
} from "@/@types/data/orderV3";
import { ProductIdentity } from "@/@types/data/collections";
import { convertProductType } from "@/providers/utils";

const { API_BASE } = siteConfig;

/**
 * start makes a call to the v3 api to initialise a new purchase request.
 * This will return either a redirectUrl, and/or error object.
 *
 * The redirectUrl returned will link out to the webpay systems.
 * If no redirectUrl is returned, we can assume the intention is for us to continue to our internal payment screens (WIP)
 *
 * @returns <StartOrderV3Response>
 */
export const startOrder = async (
  locale: string,
  purchaseData: PurchaseData,
  trafficSource: string,
  { sessionId, affiliateId }: { sessionId: string | undefined; affiliateId: string | undefined },
  failRedirectUrl?: string,
): Promise<StartOrderV3Response> => {
  const { products } = purchaseData;
  if (products.length && products.length !== 1) {
    throw new Error("Multiple products are currently unsupported");
  }

  const { gameId, productId, productSlug, productType, thumbnailUrl, name, type } = products[0];

  const endpoint = `${API_BASE}/v3/orders/start-order`;

  const headers: AxiosRequestHeaders = new AxiosHeaders();

  // Try to get session & affiliate Id

  sessionId && (headers["X-Session-ID"] = sessionId);
  affiliateId && (headers["X-Affiliate-ID"] = affiliateId);

  // Ensure we have a trailing slash on our URL, and we remove any query parameters passed in originally.
  const productUrl = `/${locale}/${gameId.toLowerCase()}/${productSlug}/`;
  const currency = VirtualCurrencyCodeByProductGame[gameId]; // TODO: Multi Game (Agnostic) Support!
  const product: ProductIdentity = {
    gameId,
    productId,
    productType: convertProductType(productType),
  };

  const payload: StartOrderV3Request = {
    currency,
    title: name,
    subtitle: type,
    redirectUrl: `/${locale}/${gameId.toLowerCase()}/success/`,
    thumbnailUrl: thumbnailUrl, // Bundle sends `null`
    failRedirectUrl: failRedirectUrl ?? `${productUrl}`,
    locale,
    ...(trafficSource ? { trafficSource } : null),
    product,
  };

  sendAnalytics(AnalyticsEvent.PURCHASE_REQUEST, { game_name: GAME_NAME_BY_GAMEID[gameId], product_slug: productSlug });

  try {
    const purchaseResponse = await axios.post<StartOrderV3Response>(endpoint, payload, {
      headers,
      withCredentials: true,
    });

    const status = purchaseResponse.status;
    if (status === 200) {
      const { status, context } = purchaseResponse.data as StartOrderV3Response;
      return { status, context };
    } else {
      logger("Purchase call failed with non-200 status", undefined, "error");
      const { errorCode } = purchaseResponse.data as HermesError;
      return { error: { errorCode, status }, status: undefined, context: undefined };
    }
  } catch (e) {
    /* istanbul ignore next: else */
    if (e instanceof Error) {
      logger("Purchase call threw an exception", e, "error", { gameId, productSlug });
    }
    throw e;
  }
};

/**
 * confirmOrder calls out to /v3/orders/confirm-order to fulfil an order.
 * This endpoint assumes the player is already aware that this would complete the order.
 *
 * @param accessToken
 * @param game
 * @param productId
 * @param trafficSource
 * @returns
 */
export const confirmOrder = async (
  product: ProductIdentity,
  trafficSource: string,
  {
    sessionId,
    affiliateId,
  }: {
    sessionId: string | undefined;
    affiliateId: string | undefined;
  },
): Promise<ConfirmOrderV3Response> => {
  const endpoint = `${API_BASE}/v3/orders/confirm-order`;

  const vcc = VirtualCurrencyCodeByProductGame[product.gameId];
  const headers: Record<string, string> = {};

  // Try to get session & affiliate Id
  sessionId && (headers["X-Session-ID"] = sessionId);
  affiliateId && (headers["X-Affiliate-ID"] = affiliateId);

  const productIdentity: ProductIdentity = {
    ...product,
    productType: convertProductType(product.productType),
  };

  const payload: ConfirmOrderV3Request = {
    ...(trafficSource ? { trafficSource } : null),
    currency: vcc,
    product: productIdentity,
  };

  try {
    const response = await axios.post<ConfirmOrderV3Response>(endpoint, payload, { headers, withCredentials: true });

    if (response.status === 200) {
      const { order_id, email } = response.data;
      return { order_id, email, error: false };
    }
  } catch (e) {
    logger("Unable to confirm order", e as Error, "error", { product });
  }
  return { error: true };
};

/* Purchase virtual currency bundle
 * Check if players are logged-in
 * Sends a request to the API
 * Redirects players to the Payments flow
 */
export const purchaseVcBundle = async (locale: string, gameId: ProductGame): Promise<ModalTypes | undefined> => {
  const vcc = VirtualCurrencyCodeByProductGame[gameId];

  // API request properties
  const endpoint = `${API_BASE}/v3/orders/start-virtual-currency-order`;
  const payload: StartVirtualCurrencyOrderV3Request = {
    gameId,
    currency: vcc,
    failRedirectUrl: window.location.pathname || "/", // Note: Player's current page https://stackoverflow.com/a/6944772/1428241
    locale: locale,
    redirectUrl: `/${locale}/${gameId.toLowerCase()}/${PageName.SUCCESS}/`,
  };

  // Make the API request
  try {
    // TODO: Multi Game (Agnostic) Support!
    const topUpResponse = await axios.post<StartOrderV3Response>(endpoint, payload, { withCredentials: true });
    const status = topUpResponse.status;
    if (status == 200) {
      const { context } = topUpResponse.data as StartOrderV3Response;
      window.location.href = (context as OrderContext).redirect_url;
    } else if (status === 401) {
      // Invalid token
      logger(`startVirtualCurrencyOrder request has failed with 401 status.`, undefined, "error");
      return ModalTypes.UNAUTHORIZED;
    } else {
      // Could be 404, 500, or 400 errors
      const { errorCode } = topUpResponse.data as HermesError;
      logger(
        `startVirtualCurrencyOrder request has failed with non-200 status. Error code: ${errorCode}`,
        undefined,
        "error",
      );
      return ModalTypes.SERVER_ERROR;
    }
  } catch (e) {
    /* istanbul ignore next: else */
    if (e instanceof Error) {
      logger("startVirtualCurrencyOrder request threw an exception", e, "error", { gameId });
    }
    return ModalTypes.SERVER_ERROR;
  }
};
